import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import type { MetricValue } from "./daily-monitoring-metrics";

import { PPGQualityFlag } from "../../api";
import { Tooltip } from "../../components/ui-kit/base/tooltip";
import { resolveEnumLabel } from "../../components/ui-kit/fields/utils";
import { ppgQuality } from "../../constants/enum-to-text";

/**
 * Signal-quality indicator for the latest PPG's computed SQI. Shows the state
 * as a coloured bullet rather than the raw number (which is only meaningful
 * once you know the bands); the value itself sits in the tooltip.
 */
export function SignalIndicator({
  sqi,
  flag,
}: {
  sqi: MetricValue;
  flag: PPGQualityFlag | null;
}) {
  if (!sqi) return <span className="text-muted-foreground">–</span>;

  const state = signalState(sqi.value, flag);
  const label = i18n._(state.label);
  const sqiValue = sqi.value.toFixed(1);
  const flagLabel =
    flag != null && flag !== PPGQualityFlag.ok
      ? resolveEnumLabel(ppgQuality[flag])
      : "";
  const bullet = (
    <span className={`inline-block size-2.5 rounded-full ${state.className}`} />
  );
  return (
    <Tooltip
      content={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 font-semibold">
            {bullet}
            {label}
          </div>
          <div className="font-normal">
            <Trans>SQI {sqiValue}</Trans>
          </div>
          <div className="font-normal">{i18n._(state.meaning)}</div>
          {flag != null && flag !== PPGQualityFlag.ok && (
            <div className="font-normal">
              <Trans>PPG flag: {flagLabel}</Trans>
            </div>
          )}
        </div>
      }
    >
      <span className="inline-flex items-center" aria-label={label}>
        {bullet}
      </span>
    </Tooltip>
  );
}

/**
 * Flags that take the measurement out of play regardless of how the SQI scores
 * — grading it green would be misleading. `unknown` means quality hasn't been
 * evaluated yet. (The `ignore_data` / `manual_value_override` states belong to
 * PPG-*derived* data, not to the PPG signal itself.)
 */
const UNGRADED_FLAGS: PPGQualityFlag[] = [
  PPGQualityFlag.unknown,
  PPGQualityFlag.short_signal,
  PPGQualityFlag.test_measurement,
];

/**
 * SQI bands as the backend applies them when it auto-flags PPG quality (see the
 * quality-flag legend on the patient PPG page): ≤ 3.0 is flagged `data error`,
 * 3.0–5.0 `low signal quality`, above that the signal is fine.
 */
function signalState(sqi: number, flag: PPGQualityFlag | null) {
  if (flag != null && UNGRADED_FLAGS.includes(flag)) {
    return {
      className: "bg-muted-foreground",
      label: msg`Not graded`,
      meaning:
        flag === PPGQualityFlag.unknown
          ? msg`Signal quality has not been evaluated yet.`
          : msg`Excluded from analyses, so the SQI is not graded.`,
    };
  }
  if (sqi <= 3) {
    return {
      className: "bg-danger-solid",
      label: msg`Poor signal`,
      meaning: msg`Flagged as data error — not used in any automatic algorithms.`,
    };
  }
  if (sqi <= 5) {
    return {
      className: "bg-warning-solid",
      label: msg`Low signal quality`,
      meaning: msg`Used by the algorithms, but results inherit a low-quality flag. Worth a manual check.`,
    };
  }
  return {
    className: "bg-success-solid",
    label: msg`Signal OK`,
    meaning: msg`Good to use for analyses.`,
  };
}
